import { createBaseRecord } from "../records";
import {
  findHeaderRow,
  findSheetName,
  formatDateValue,
  getSheetRows,
  normalizeHeader,
  normalizeText,
  sheetRowOffset,
} from "../workbook";

export const TNT = "TNT";


const DETAIL_SHEET_NAME = "Exceptions";

const REQUIRED_HEADERS = [
  "consignment",
  "pick up date",
  "expected delivery",
  "actual delivery",
  "comments",
];

export const badgeClass = "bg-violet-100 text-violet-700 ring-violet-200";

const CATEGORY_RULES = {
  "wrong address": [true, "Client"],
  unsendables: [true, "Client"],
  "requests for change": [true, "Client"],
  "wrong edi data": [true, "Client"],
  "consignee not present": [true, "Client"],
  "weather, accidents, third party": [true, "Other"],
  "shipment refused": [true, "Other"],
  "dangerous goods, industrial issues": [true, "Other"],
  "dangerous goods, industrial issue": [true, "Other"],
  "late linehaul": [false, "TNT"],
  "missed scheduled linehaul": [false, "TNT"],
  "missed schedule linehaul": [false, "TNT"],
  misrouted: [false, "TNT"],
  "split deliveries": [false, "TNT"],
  "depot processing": [false, "TNT"],
  "depot procesing": [false, "TNT"],
  damages: [false, "TNT"],
  "other failures": [false, "TNT"],
};

function resolveDetailSheet(workbook) {
  const sheetNames = workbook.SheetNames || [];
  const namedSheet = findSheetName(sheetNames, DETAIL_SHEET_NAME);

  const candidates = [
    ...(namedSheet ? [namedSheet] : []),
    ...[...sheetNames].reverse().filter((name) => name !== namedSheet),
  ];

  for (const name of candidates) {
    const sheet = workbook.Sheets[name];
    const headerRow = findHeaderRow(sheet, REQUIRED_HEADERS);
    if (headerRow) {
      return {
        sheetName: name,
        headerRow,
        excelHeaderRow: sheetRowOffset(sheet) + headerRow,
      };
    }
  }
  return { sheetName: null, headerRow: 0, excelHeaderRow: 0 };
}

export function classifyCategory(category) {
  const rule = CATEGORY_RULES[normalizeHeader(category)];
  if (!rule) {
    return {
      isHit: false,
      exceptionType: "Unknown",
      ruleApplied: "TNT unknown exception category treated as miss",
    };
  }

  const [isHit, exceptionType] = rule;
  return {
    isHit,
    exceptionType,
    ruleApplied: isHit
      ? "TNT accepted exception converted to hit"
      : "TNT-attributable exception remains miss",
  };
}

export function detect(workbook) {
  return Boolean(resolveDetailSheet(workbook).sheetName);
}

function parseRows(rows, sheetName, excelHeaderRow) {
  const records = [];

  rows.forEach((row, index) => {
    const consignmentId = normalizeText(row.consignment);
    if (!consignmentId) return;

    const category = normalizeText(row.comments);
    const { isHit, exceptionType, ruleApplied } = classifyCategory(category);

    const hasCompletedDate = Boolean(formatDateValue(row["actual delivery"]));

    records.push(
      createBaseRecord({
        carrier: TNT,
        sourceSheet: sheetName,
        rowNumber: excelHeaderRow + 1 + index,
        consignmentId,
        despatchDate: row["pick up date"],
        eta: row["expected delivery"],
        completedDate: hasCompletedDate ? row["actual delivery"] : "",
        automatedKpiHit: isHit,
        carrierKpiExplanation: category,
        ruleApplied,
        rawStatus: category,
        forceMajeureReason: exceptionType === "Other" ? category : "",
        exclusionCategory: category,
      })
    );
  });

  return records;
}

export function parse(workbook, file) {
  const { sheetName, headerRow, excelHeaderRow } = resolveDetailSheet(workbook);

  if (!sheetName) {
    throw new Error(
      `File "${file.name}" is not a valid TNT report. Required sheet: ${DETAIL_SHEET_NAME}.`
    );
  }

  const rows = getSheetRows(workbook.Sheets[sheetName], headerRow - 1, true);

  return {
    records: parseRows(rows, sheetName, excelHeaderRow),
    sheetSummaries: [{ name: sheetName, rowCount: rows.length }],
  };
}
